import { Link } from '@tanstack/react-router';
import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import Icon from '../ui/Icon';
import type { Folder } from '../../types/api';
import type { DropTargetData } from '../../contexts/BookmarkDndContext';
import { formatCount } from '../../utils/numberUtils';
import { useContextMenu } from '../../hooks/useContextMenu';
import SidebarItemMenu from './SidebarItemMenu';

export interface FolderItemProps {
  folder: Folder;
  isSelected: boolean;
}

function FolderItem({ folder, isSelected }: FolderItemProps) {
  const { id, slug } = folder;
  const contextMenu = useContextMenu();

  const dropData: DropTargetData = {
    type: 'folder',
    folderId: folder.id,
  };

  const { setNodeRef, isOver } = useDroppable({
    id: `folder-drop-${folder.id}`,
    data: dropData,
    disabled: isSelected, // Don't allow dropping on current folder
  });

  return (
    <li
      id={`folder_${id}`}
      data-folder-id={id}
      className="folder-item relative"
      onContextMenu={contextMenu.handleContextMenu}
    >
      <Link
        ref={setNodeRef}
        to="/folder/$slug"
        params={{ slug }}
        className={clsx(
          'side-item folder-link',
          !isSelected && 'transition-all duration-150',
          isOver && !isSelected && 'drop-target-highlight',
        )}
        data-active={isSelected ? 'true' : undefined}
        data-droptarget={!isSelected ? 'true' : undefined}
      >
        <Icon name={folder.public ? 'folder-public' : 'folder'} />
        <span className="folder-name max-w-[138px] truncate leading-snug">
          {folder.displayTitle}
        </span>
        {folder.count > 0 && (
          <span className="side-nav-count" title={String(folder.count)}>
            {formatCount(folder.count)}
          </span>
        )}
      </Link>

      {isSelected && (
        <a
          className="folder-edit-icon hidden"
          data-modal="folder-edit-modal"
          data-folder-id={folder.id}
          data-folder-action={`/folder/edit/${folder.id}`}
          data-folder-title={folder.displayTitle}
          data-folder-sync-to-iphone={folder.syncToMobile ? 'true' : 'false'}
          data-folder-public={folder.public ? 'true' : 'false'}
          href="#"
        >
          <i className="icon icon-pencil"></i>
        </a>
      )}

      {contextMenu.anchorPosition && (
        <span className="absolute" aria-hidden>
          <SidebarItemMenu
            folder={folder}
            isActive={isSelected}
            anchorPosition={contextMenu.anchorPosition}
            onOpenChange={contextMenu.handleOpenChange}
          />
        </span>
      )}
    </li>
  );
}

export default FolderItem;
